import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import React, { useMemo, useState } from 'react';
import { DemeterMarket } from '../../../../../Generated/Raven-Demeter';
import IconButton from '../../../../Components/Form/Buttons/IconButton';
import formattingService from '../../../../Services/Formatting/FormattingService';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import styles from './MarketAccess.module.scss';
import { calculateNumberOfDaysRemaining, expiringSoonNumberOfDays, MarketAccessExpiration, MarketAccessPastGrant } from './MarketAccessDefinitions';
import MarketAccessExpirationModal from './MarketAccessExpirationModal';

interface IMarketAccessExpirationListProps {
    expirations: MarketAccessExpiration[];
    pastGrants: MarketAccessPastGrant[];
    accountExpirationDate?: Date;
    handleExpirationsChange: (expirations: MarketAccessExpiration[]) => void;
}

const MarketAccessExpirationList: React.FC<IMarketAccessExpirationListProps> = (props: IMarketAccessExpirationListProps) => {
    const [translations] = useLanguage();
    const [marketBeingEdited, setMarketBeingEdited] = useState<DemeterMarket>();

    const expirationBeingEdited = useMemo(
        () => props.expirations.find((expiration) => expiration.market === marketBeingEdited),
        [props.expirations, marketBeingEdited],
    );

    const handleSave = (updatedExpiration: MarketAccessExpiration) => {
        props.handleExpirationsChange(props.expirations.map((expiration) => (expiration.market === updatedExpiration.market ? updatedExpiration : expiration)));
        setMarketBeingEdited(undefined);
    };

    const handleApplyToAllMarkets = (updatedExpiration: MarketAccessExpiration) => {
        props.handleExpirationsChange(props.expirations.map((expiration) => ({ ...updatedExpiration, market: expiration.market })));
        setMarketBeingEdited(undefined);
    };

    // Once the grant is a week or less from ending the remaining time is called out rather than left to be read off the date.
    const buildDaysRemainingText = (numberOfDaysRemaining: number): string => {
        if (numberOfDaysRemaining < 0) {
            return translations.users.marketAccessStatus.Expired;
        }

        if (numberOfDaysRemaining === 0) {
            return translations.users.marketAccess.expiresToday;
        }

        const daysRemainingText =
            numberOfDaysRemaining === 1
                ? translations.users.marketAccess.oneDayRemaining
                : translations.users.marketAccess.daysRemaining.replace('{count}', String(numberOfDaysRemaining));

        return numberOfDaysRemaining <= expiringSoonNumberOfDays ? `${translations.users.marketAccess.expiringSoon} · ${daysRemainingText}` : daysRemainingText;
    };

    if (props.expirations.length === 0 && props.pastGrants.length === 0) {
        return null;
    }

    return (
        <div className={styles.market_access_list_container}>
            <h3 className={styles.market_access_list_title}>{translations.users.marketAccess.title}</h3>
            <ul className={styles.market_access_list}>
                {props.expirations.map((expiration) => {
                    const isTemporary = expiration.expirationOption !== 'permanent';
                    const isMissingDate = isTemporary && !expiration.expirationDate;
                    const numberOfDaysRemaining =
                        isTemporary && expiration.expirationDate ? calculateNumberOfDaysRemaining(expiration.expirationDate) : undefined;
                    const isEndingSoonOrEnded = numberOfDaysRemaining !== undefined && numberOfDaysRemaining <= expiringSoonNumberOfDays;

                    let periodClassName = styles.market_access_period;
                    if (isMissingDate) {
                        periodClassName = `${styles.market_access_period} ${styles.market_access_period_incomplete}`;
                    } else if (isTemporary) {
                        periodClassName = `${styles.market_access_period} ${styles.market_access_period_temporary}`;
                    }

                    const setAccessPeriodLabel = `${translations.users.marketAccess.setAccessPeriod} ${translations.market[expiration.market]}`;

                    let periodText = translations.users.marketAccess.permanent;
                    if (isMissingDate) {
                        periodText = translations.users.marketAccess.dateNeeded;
                    } else if (isTemporary) {
                        periodText = `${translations.users.marketAccess.endsOn} ${formattingService.toLongDayMonthYear(expiration.expirationDate)}`;
                    }

                    return (
                        <li
                            key={expiration.market}
                            className={
                                isTemporary ? `${styles.market_access_list_item} ${styles.market_access_list_item_temporary}` : styles.market_access_list_item
                            }
                        >
                            <span className={styles.market_access_market_name}>{translations.market[expiration.market]}</span>
                            <span className={periodClassName}>{periodText}</span>
                            {numberOfDaysRemaining !== undefined && (
                                <span
                                    className={
                                        isEndingSoonOrEnded
                                            ? `${styles.market_access_days_remaining} ${styles.market_access_days_remaining_ending_soon}`
                                            : styles.market_access_days_remaining
                                    }
                                >
                                    {buildDaysRemainingText(numberOfDaysRemaining)}
                                </span>
                            )}
                            <IconButton
                                size="small"
                                tooltip={setAccessPeriodLabel}
                                handleClick={() => setMarketBeingEdited(expiration.market)}
                                track={{ eventCategory: 'User', eventAction: 'MarketAccessExpirationOpened', eventValue: expiration.market }}
                            >
                                <CalendarTodayIcon className={styles.market_access_calendar_icon} titleAccess={setAccessPeriodLabel} />
                            </IconButton>
                        </li>
                    );
                })}
            </ul>
            {props.pastGrants.length > 0 && (
                <>
                    <h4 className={styles.market_access_past_grants_title}>{translations.users.marketAccess.pastGrantsTitle}</h4>
                    <ul className={styles.market_access_list}>
                        {props.pastGrants.map((pastGrant) => (
                            <li key={pastGrant.market} className={`${styles.market_access_list_item} ${styles.market_access_list_item_past}`}>
                                <span className={styles.market_access_market_name}>{translations.market[pastGrant.market]}</span>
                                <span className={styles.market_access_period}>
                                    {pastGrant.endDate
                                        ? `${translations.users.marketAccess.endedOn} ${formattingService.toLongDayMonthYear(pastGrant.endDate)}`
                                        : ''}
                                </span>
                                <span className={styles.market_access_past_grant_status}>{translations.users.marketAccessStatus[pastGrant.status]}</span>
                            </li>
                        ))}
                    </ul>
                </>
            )}
            {expirationBeingEdited && (
                <MarketAccessExpirationModal
                    showModal
                    expiration={expirationBeingEdited}
                    accountExpirationDate={props.accountExpirationDate}
                    handleSave={handleSave}
                    handleApplyToAllMarkets={handleApplyToAllMarkets}
                    handleCancel={() => setMarketBeingEdited(undefined)}
                />
            )}
        </div>
    );
};

export default MarketAccessExpirationList;
